import { parseDateString } from './dailyPuzzle';
import { GAME_2048_THEMES } from './game2048Themes';

export type Game2048DisplayMode = 'numbers' | 'pictures';
export interface Game2048Appearance {
  themeId: string;
  images: Readonly<Record<number, string>>;
}

export const GAME_2048_DISPLAY_KEY = 'game-2048-display';

export const parse2048DisplayMode = (value: string | null): Game2048DisplayMode => {
  if (value === null) {
    return 'pictures';
  }
  if (value !== 'numbers' && value !== 'pictures') {
    throw new Error('Invalid 2048 display preference: ' + value);
  }
  return value;
};

const factorial = (value: number): bigint => {
  let result = 1n;
  for (let i = 2; i <= value; i += 1) {
    result *= BigInt(i);
  }
  return result;
};

const visualSeed = (date: string): number => {
  let hash = 2166136261;
  for (const character of '2048-art-v1:' + date) {
    hash = Math.imul(hash ^ character.charCodeAt(0), 16777619) >>> 0;
  }
  return hash;
};

export const getEligible2048Themes = (date: string) => {
  parseDateString(date);
  return GAME_2048_THEMES.filter(theme => {
    if (!theme.startDate) {
      return true;
    }
    parseDateString(theme.startDate);
    return date >= theme.startDate;
  });
};

export const get2048Appearance = (date: string): Game2048Appearance => {
  const eligibleThemes = getEligible2048Themes(date);
  const day = BigInt(Date.parse(date + 'T00:00:00Z') / 86400000);
  if (eligibleThemes.length === 0) {
    throw new Error('No 2048 themes available for date: ' + date);
  }
  const theme = eligibleThemes[visualSeed(date) % eligibleThemes.length];
  if (theme.images.length !== 16 || new Set(theme.images).size !== 16) {
    throw new Error('Invalid 2048 theme: ' + theme.id);
  }
  const remaining = [...theme.images];
  const period = factorial(16);
  // The multiplier is coprime to 16!, so ranks repeat only after 16! days.
  let rank = ((day * 12930600000001n + 2166136261n) % period + period) % period;
  const images: Record<number, string> = {};
  for (let exponent = 1; exponent <= 16; exponent += 1) {
    const block = factorial(remaining.length - 1);
    const index = Number(rank / block);
    rank %= block;
    const [image] = remaining.splice(index, 1);
    images[2 ** exponent] = `/assets/images/2048/${theme.id}/${image}.webp`;
  }
  return { themeId: theme.id, images };
};
